/* eslint-disable import/no-cycle */
import { subscriptionTypes } from '@constants/index';
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import { useRouter } from 'next/router';
import Typography from 'aquilla/core/atoms/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Image from '@base/FutureImage';
import Cross from 'aquilla/icons/misc/Cross';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import useReferralCodeSuggestions from '@cont/Checkout/useReferralCodeSuggestions';
import ReferralSuggestions from '@cont/Checkout/ReferralSuggestions';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { getGoalDurationMetaInfo } from '@cont/Checkout/apiHelper';
import {
  ApplyButton,
  ApplyText,
  InfoWrapper,
  ReferralBannerWrapper,
  ReferralRightWrapper,
  StyledDrawer,
  updateUrlWithReferral
} from '../NormalPlanSelectionFlow/NormalPlanSelectionPage';
import PlanDetailCard from '../BaseComponents/PlanDetailCard';
import { FLOWSTEPS, isUpgradeRefundApplicable } from '../PlanSelection';

const UpgradePlanSelectionFlowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const Title = styled(Typography)`
  width: 300px;
`;

const PlanDetailCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

interface DurationInfoType {
  durationInfo: Record<string, any>;
}

const UpgradePlanSelectionPage = ({
  onSubmit,
  goalDetails,
  pricings,
  setBaseFlowSteps,
  pricingLoading,
  setPreviousStep,
  referralCode,
  setReferralCode,
  selectedPlan,
  setSelectedPlan,
  purchaseDetails,
  referralError,
  validateReferralInProgress,
  preFilteredSubscriptionLists = null,
  referralValid,
  referralAllLoading,
  discountedPricings = [],
  showBackButton
}) => {
  const { query: { goalUID = '', referral_code = '', goalSlug = '' } = {} } =
    useRouter();
  const [planSelection, setPlanSelection] = useState<number>(0);
  const [isApplyCouponDrawerOpen, setIsApplyCouponDrawerOpen] =
    useState<boolean>(false);
  const [goalDurationData, setGoalDurationData] =
    useState<Array<Record<string, any> | undefined>>();

  useEffect(() => {
    (async () => {
      const data = await getGoalDurationMetaInfo(goalUID);
      if (!data?.error) setGoalDurationData(data);
    })();
  }, [goalUID]);

  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));
  const {
    checkout_dates,
    checkout_dates_with_benefits: {
      ends_at = '',
      starts_at = ''
    } = checkout_dates ?? {}
  } = selectedPlan ?? {};

  useEffect(() => {
    if (referral_code) setReferralCode(referral_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral_code]);

  useEffect(() => {
    if (!isApplyCouponDrawerOpen)
      setSelectedPlan(preFilteredSubscriptionLists[0]);
  }, [
    isApplyCouponDrawerOpen,
    preFilteredSubscriptionLists,
    setSelectedPlan,
    referralCode
  ]);

  useEffect(() => {
    if (!pricingLoading && !preFilteredSubscriptionLists?.length) {
      setBaseFlowSteps(FLOWSTEPS.ISEXTENDFLOW);
    }
  }, [preFilteredSubscriptionLists, pricingLoading, setBaseFlowSteps]);

  useFirstRenderSkip(() => {
    if (!referralAllLoading || !referralCode)
      updateUrlWithReferral(goalSlug, goalUID, referralCode, referralValid);
  }, [goalSlug, goalUID, referralCode, referralValid, referralAllLoading]);

  const closeCouponsDrawer = useCallback(() => {
    setIsApplyCouponDrawerOpen(false);
  }, []);

  const { referralCodes = [] } = useReferralCodeSuggestions(
    preFilteredSubscriptionLists?.length > 0 &&
      preFilteredSubscriptionLists[0]?.uid
  );

  const firstReferralCode =
    referralCodes.length > 0
      ? referralCodes?.find((code) => code?.isActive === true)
      : {};

  const { referral: { discountPercent = '' } = {} } =
    purchaseDetails?.subscription ?? {};

  const getReferralCodeTitle = () => {
    if (referralValid && referralCode) return `Applied ${referralCode}`;
    if (firstReferralCode?.code) return `Use ${firstReferralCode?.code}`;
    return 'Have a referral code?';
  };

  return (
    <>
      <UpgradePlanSelectionFlowWrapper>
        {showBackButton && (
          <Title variant="h2">
            Extend duration for your {goalDetails?.name} with{' '}
            <span style={{ color: 'var(--color-i-orange)' }}>Iconic</span>
          </Title>
        )}
        <ReferralBannerWrapper>
          <Image
            src="newPlanSelection/referralTicket.svg"
            width={40}
            height={40}
            alt="Referral Ticket"
          />
          <ReferralRightWrapper>
            <InfoWrapper>
              <Typography variant="h6">{getReferralCodeTitle()}</Typography>
              {(firstReferralCode?.discountPercent || discountPercent) && (
                <Typography
                  variant="p3"
                  color={discountPercent && 'var(--color-i-brown-14)'}
                >
                  {referralValid && discountPercent
                    ? `Saving ${discountPercent}%`
                    : `Get ${firstReferralCode?.discountPercent}% off on all subscriptions`}
                </Typography>
              )}
            </InfoWrapper>
            {referralValid && referralCode ? (
              <Cross
                width={24}
                height={24}
                onClick={() => {
                  setReferralCode('');
                }}
              />
            ) : (
              <ApplyButton
                onClick={() => {
                  setIsApplyCouponDrawerOpen(true);
                }}
              >
                <ApplyText variant="p3">Apply</ApplyText>
                <RightChevron width={18} height={18} />
              </ApplyButton>
            )}
          </ReferralRightWrapper>
        </ReferralBannerWrapper>
        <PlanDetailCardWrapper>
          {preFilteredSubscriptionLists.map((subscription, index) => {
            const goalDurationInfo = goalDurationData
              ? goalDurationData?.find((info) => {
                  return (
                    +info?.duration === subscription?.duration &&
                    info?.goalUid === goalUID
                  );
                })
              : [];
            return (
              <PlanDetailCard
                key={subscription.uid}
                subscription={subscription}
                index={index}
                planSelection={planSelection}
                onClick={() => {
                  setPlanSelection(index);
                  setSelectedPlan(subscription);
                }}
                onSubmit={(plan) => {
                  onSubmit(plan);
                }}
                isReferralAdded={referralValid && !!referralCode}
                showDuration
                goalDurationInfo={goalDurationInfo as DurationInfoType}
                discountedPricings={discountedPricings}
              />
            );
          })}
        </PlanDetailCardWrapper>
      </UpgradePlanSelectionFlowWrapper>
      <StyledDrawer
        drawerState={isApplyCouponDrawerOpen}
        hideCloseButton
        onClose={closeCouponsDrawer}
      >
        <ReferralSuggestions
          onClose={closeCouponsDrawer}
          onApply={(referralCode) => {
            setReferralCode(referralCode);
          }}
          referralCode={referralCode}
          validateReferralInProgress={validateReferralInProgress}
          isReferralValid={referralCode && !referralError}
          referralMessage={referralError}
          subscriptionUid={selectedPlan?.uid}
        />
      </StyledDrawer>
    </>
  );
};

export default UpgradePlanSelectionPage;
